import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  passwordPolicyIssues,
  passwordRules,
  registerUserInputSchema,
} from '@orbit-sync/shared-types';
import { useAuthStore } from '../auth/authStore.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Field } from '../components/Field.js';
import { FormBanner } from '../components/FormBanner.js';
import { Icon } from '../components/Icon.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import {
  errorMessage,
  fieldErrorsFrom,
  isApiRequestError,
  type FieldErrors,
} from '../lib/api/index.js';

export function RegisterPage() {
  const register = useAuthStore((state) => state.register);
  const pending = useAuthStore((state) => state.pending);
  const navigate = useNavigate();
  const settings = useSettingsStore((state) => state.settings);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);

    const parsed = registerUserInputSchema.safeParse({ email, displayName, password });
    const errors: FieldErrors = {};

    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        errors[path] ??= issue.message;
      }
    }

    // The administrator's rules, checked here only to fail early - the server
    // applies the same policy with the same function.
    const issues = passwordPolicyIssues(password, settings);

    if (issues.length > 0) {
      errors.password ??= `Password needs: ${issues.join(', ').toLowerCase()}.`;
    }

    if (displayName.trim().length > settings.maxDisplayNameLength) {
      errors.displayName ??= `At most ${settings.maxDisplayNameLength} characters.`;
    }

    if (!parsed.success || Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});

    try {
      await register(parsed.data);
      await navigate('/', { replace: true });
    } catch (error) {
      // 409 is the duplicate-email case. Attaching it to the email input is far
      // more useful than a banner the user has to map back to a field themselves.
      if (isApiRequestError(error) && error.status === 409) {
        setFieldErrors({ email: 'That email is already registered.' });
        return;
      }

      setFieldErrors(fieldErrorsFrom(error));
      setFormError(errorMessage(error));
    }
  }

  const rules = passwordRules(password, settings);

  if (!settings.allowRegistration) {
    return (
      <AuthLayout>
        <h1>Sign-up is closed</h1>
        <p className="subtitle">
          New accounts are not being accepted right now. Ask your administrator for an account.
        </p>
        <p className="form-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form
        method="post"
        action="/register"
        onSubmit={(event) => void handleSubmit(event)}
        noValidate
      >
        <h1>Create an account</h1>
        <p className="subtitle">A few details and you are in.</p>

        <FormBanner message={formError} />

        <Field
          label="Display name"
          name="displayName"
          autoComplete="name"
          dir="auto"
          maxLength={settings.maxDisplayNameLength}
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          error={fieldErrors.displayName}
          required
        />

        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email}
          required
        />

        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          required
        />

        <ul className="password-rules" aria-label="Password requirements">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className={rule.met ? 'password-rule password-rule--met' : 'password-rule'}
            >
              <Icon name={rule.met ? 'check' : 'x'} size={12} />
              {rule.label}
            </li>
          ))}
        </ul>

        <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={pending}>
          {pending ? <span className="btn-spinner" /> : null}
          {pending ? 'Creating account…' : 'Create account'}
        </button>

        <p className="form-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
